import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { api } from "../api";
import { openPhpMyAdmin } from "../lib/pma";
import type { DatabaseUser, HostingDatabase, PanelUser } from "../types";

type DbTab = "databases" | "users" | "wizard" | "import" | "pma";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Premium Databases / SQL Services (redesign_part3.md).
 */
export function DatabasesPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const operator = staff || reseller;
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? ""
      : reseller
        ? provisioned[0]?.id || ""
        : currentUser.provisionStatus === "ready"
          ? currentUser.id
          : ""
  );
  const [databases, setDatabases] = useState<HostingDatabase[]>([]);
  const [dbUsers, setDbUsers] = useState<DatabaseUser[]>([]);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [shownPass, setShownPass] = useState("");
  const [tab, setTab] = useState<DbTab>("databases");
  const [search, setSearch] = useState("");
  const [menuId, setMenuId] = useState<string | null>(null);

  // Create DB drawer
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("wp");
  const [createCharset, setCreateCharset] = useState("utf8mb4");
  const [createCollation, setCreateCollation] = useState(
    "utf8mb4_unicode_ci"
  );
  const [createUserMode, setCreateUserMode] = useState<"new" | "existing" | "none">(
    "new"
  );
  const [createUserLogin, setCreateUserLogin] = useState("");
  const [createUserPass, setCreateUserPass] = useState("");
  const [createExistingUserId, setCreateExistingUserId] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  // Wizard
  const [wizStep, setWizStep] = useState(1);
  const [wizDb, setWizDb] = useState("wp");
  const [wizUser, setWizUser] = useState("");
  const [wizPass, setWizPass] = useState("");
  const [wizDbId, setWizDbId] = useState("");
  const [wizBusy, setWizBusy] = useState(false);

  // Console / import
  const [queryDbId, setQueryDbId] = useState("");
  const [sql, setSql] = useState("SELECT 1 AS ok;");
  const [queryResult, setQueryResult] = useState<{
    columns: string[];
    rows: unknown[][];
  } | null>(null);
  const [importSql, setImportSql] = useState("");

  const load = useCallback(async () => {
    if (!userId && !staff) return;
    setError("");
    try {
      const dbPath = userId
        ? `/api/v1/databases?userId=${encodeURIComponent(userId)}`
        : "/api/v1/databases";
      const userPath = userId
        ? `/api/v1/databases/users?userId=${encodeURIComponent(userId)}`
        : "/api/v1/databases/users";
      const d = await api<{ items: HostingDatabase[] }>(dbPath);
      setDatabases(d.items || []);
      const u = await api<{ items: DatabaseUser[] }>(userPath);
      setDbUsers(u.items || []);
      const s = await api<{ status: Record<string, unknown> }>(
        "/api/v1/databases/status"
      );
      setStatus(s.status || null);
      if (d.items?.[0] && !queryDbId) setQueryDbId(d.items[0].id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, queryDbId, staff]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === userId),
    [accounts, userId]
  );

  const totalBytes = useMemo(
    () => databases.reduce((s, d) => s + (d.sizeBytes || 0), 0),
    [databases]
  );

  const mysqlAvailable = Boolean(status?.mysqlAvailable);
  const engine = String(status?.engine || status?.mode || "—");
  const isShadow =
    !mysqlAvailable ||
    /sqlite|shadow/i.test(engine) ||
    /sqlite|shadow/i.test(String(status?.mode || ""));

  const filteredDbs = useMemo(() => {
    if (!search.trim()) return databases;
    const q = search.trim().toLowerCase();
    return databases.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.username || "").toLowerCase().includes(q)
    );
  }, [databases, search]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return dbUsers;
    const q = search.trim().toLowerCase();
    return dbUsers.filter(
      (u) =>
        u.login.toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q)
    );
  }, [dbUsers, search]);

  const openAdminer = async (databaseId?: string) => {
    setError("");
    try {
      if (staff && !userId) {
        await openPhpMyAdmin({ serverWide: true, databaseId });
        return;
      }
      await openPhpMyAdmin({
        userId: userId || undefined,
        databaseId,
        serverWide: false
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const createDatabaseFlow = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId && !staff) return;
    setCreateBusy(true);
    setError("");
    setShownPass("");
    try {
      const res = await api<{ database: HostingDatabase }>(
        "/api/v1/databases",
        {
          method: "POST",
          body: JSON.stringify({
            userId,
            name: createName,
            charset: createCharset,
            collation: createCollation
          })
        }
      );
      const dbId = res.database?.id || "";
      setMsg(`Database ${res.database?.name || createName} created`);

      if (createUserMode === "new" && createUserPass) {
        const ures = await api<{
          user: DatabaseUser;
          password?: string;
        }>("/api/v1/databases/users", {
          method: "POST",
          body: JSON.stringify({
            userId,
            login: createUserLogin || undefined,
            password: createUserPass,
            databaseId: dbId || undefined,
            remoteHost: "localhost"
          })
        });
        setShownPass(ures.password || createUserPass);
        setMsg(
          `Database ${res.database?.name} + user ${ures.user.login} ready`
        );
      }
      setCreateUserPass("");
      setShowCreate(false);
      setCreateName("wp");
      await load();
      setTab("databases");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreateBusy(false);
    }
  };

  const wizardCreateDb = async (e: FormEvent) => {
    e.preventDefault();
    setWizBusy(true);
    setError("");
    try {
      const res = await api<{ database: HostingDatabase }>(
        "/api/v1/databases",
        {
          method: "POST",
          body: JSON.stringify({ userId, name: wizDb })
        }
      );
      setWizDbId(res.database?.id || "");
      setMsg(`Database ${res.database?.name || wizDb} created`);
      setWizStep(2);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWizBusy(false);
    }
  };

  const wizardCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    setWizBusy(true);
    setError("");
    setShownPass("");
    try {
      const res = await api<{
        user: DatabaseUser;
        password?: string;
      }>("/api/v1/databases/users", {
        method: "POST",
        body: JSON.stringify({
          userId,
          login: wizUser || undefined,
          password: wizPass,
          databaseId: wizDbId || undefined,
          remoteHost: "localhost"
        })
      });
      setShownPass(res.password || wizPass);
      setMsg(`User ${res.user.login} created & granted on database`);
      setWizStep(3);
      setWizPass("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWizBusy(false);
    }
  };

  const runQuery = async (e: FormEvent) => {
    e.preventDefault();
    if (!queryDbId) return;
    setError("");
    try {
      const res = await api<{
        columns: string[];
        rows: unknown[][];
      }>(`/api/v1/databases/${queryDbId}/query`, {
        method: "POST",
        body: JSON.stringify({ sql })
      });
      setQueryResult({ columns: res.columns || [], rows: res.rows || [] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const exportDb = async (id: string, name: string) => {
    setError("");
    try {
      const res = await api<{ sql: string }>(
        `/api/v1/databases/${id}/export`
      );
      const blob = new Blob([res.sql || ""], { type: "application/sql" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.sql`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`Exported ${name}.sql`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const importDb = async (e: FormEvent) => {
    e.preventDefault();
    if (!queryDbId) return;
    setError("");
    try {
      await api(`/api/v1/databases/${queryDbId}/import`, {
        method: "POST",
        body: JSON.stringify({ sql: importSql })
      });
      setMsg("Import completed");
      setImportSql("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteDb = async (d: HostingDatabase) => {
    if (!confirm(`Delete database ${d.name}? This cannot be undone.`)) return;
    try {
      await api(`/api/v1/databases/${d.id}`, { method: "DELETE" });
      setMsg(`Deleted ${d.name}`);
      setMenuId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteUser = async (u: DatabaseUser) => {
    if (!confirm(`Delete user ${u.login}?`)) return;
    try {
      await api(`/api/v1/databases/users/${u.id}`, { method: "DELETE" });
      setMsg(`Deleted user ${u.login}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!userId && !staff) {
    return (
      <div className="rp-page">
        <div className="rp-page-head">
          <div>
            <h1 className="rp-page-title">Databases</h1>
            <p className="rp-page-sub">
              Your hosting account must be ready before databases are available.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
        <div className="rp-empty">
          <strong>No account ready</strong>
          <p>
            Ask your host to finish account setup, then return here for MySQL
            and phpMyAdmin.
          </p>
        </div>
      </div>
    );
  }

  const tabs: { id: DbTab; label: string }[] = [
    { id: "databases", label: "Databases" },
    { id: "users", label: "Database Users" },
    { id: "wizard", label: "Wizard" },
    { id: "import", label: "Import / Export" },
    { id: "pma", label: "phpMyAdmin" }
  ];

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">Databases</h1>
          <p className="rp-page-sub">
            Manage MySQL databases, users, imports and phpMyAdmin.
          </p>
        </div>
        <div className="rp-page-actions">
          {operator ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-label="Account for databases"
            >
              {staff ? <option value="">All accounts</option> : null}
              {provisioned.length === 0 ? (
                <option value="">No ready accounts</option>
              ) : null}
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void openAdminer()}
            disabled={!staff && !userId}
          >
            phpMyAdmin
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            disabled={!userId}
            onClick={() => setShowCreate(true)}
          >
            + Create Database
          </button>
        </div>
      </div>

      {selectedAccount ? (
        <p className="rp-sum-hint" style={{ marginTop: -4, marginBottom: 4 }}>
          Account <strong>{selectedAccount.username}</strong>
          {selectedAccount.primaryDomain
            ? ` · ${selectedAccount.primaryDomain}`
            : ""}
        </p>
      ) : staff && !userId ? (
        <p className="rp-sum-hint" style={{ marginTop: -4, marginBottom: 4 }}>
          Viewing server-wide databases
        </p>
      ) : null}

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {shownPass ? (
        <div className="success-banner">
          DB password (copy now): <span className="mono">{shownPass}</span>
        </div>
      ) : null}

      {isShadow ? (
        <div className="rp-alert rp-alert-warning" style={{ marginBottom: 12, flexDirection: "column", alignItems: "flex-start" }}>
          <strong>Development Database Mode</strong>
          <p style={{ margin: "6px 0 0" }}>
            {engine} / Shadow mode — production MySQL features may be limited.
          </p>
        </div>
      ) : null}

      <div className="rp-summary-strip" style={{ marginBottom: 14 }}>
        <div className="rp-sum">
          <span className="rp-sum-label">Databases</span>
          <span className="rp-sum-value">{databases.length}</span>
          <span className="rp-sum-hint">
            {userId ? "This account" : "Server-wide"}
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Database Users</span>
          <span className="rp-sum-value">{dbUsers.length}</span>
          <span className="rp-sum-hint">MySQL logins</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Storage</span>
          <span className="rp-sum-value" style={{ fontSize: 18 }}>
            {formatSize(totalBytes)}
          </span>
          <span className="rp-sum-hint">Total size</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Server</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            {engine}
          </span>
          <span className="rp-sum-hint">
            {mysqlAvailable ? "MySQL available" : "Local / shadow"}
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">phpMyAdmin</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            Available
          </span>
          <span className="rp-sum-hint">SSO launch</span>
        </div>
      </div>

      <div className="rp-panel" style={{ padding: 14, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "center"
          }}
        >
          <div>
            <div className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              DATABASE SERVER
            </div>
            <strong style={{ fontSize: 15 }}>{engine}</strong>
          </div>
          <span
            className={`rp-status ${
              mysqlAvailable && !isShadow
                ? "rp-status-ok"
                : "rp-status-warn"
            }`}
          >
            <span className="rp-status-dot" />
            {mysqlAvailable && !isShadow ? "Online" : "Limited"}
          </span>
          <span className="faint" style={{ fontSize: 13 }}>
            Host <span className="mono">localhost</span> · Port{" "}
            <span className="mono">3306</span>
          </span>
          <span className="faint" style={{ fontSize: 13 }}>
            Storage {formatSize(totalBytes)}
          </span>
          {status?.note ? (
            <span className="faint" style={{ fontSize: 12 }}>
              {String(status.note)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="rp-pkg-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`rp-pkg-tab${tab === t.id ? " is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "databases" ? (
        <>
          <div className="rp-toolbar">
            <input
              className="rp-toolbar-search"
              placeholder="Search databases…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              disabled={!userId}
              onClick={() => setShowCreate(true)}
            >
              + Create Database
            </button>
          </div>
          <div className="rp-panel">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>Database</th>
                  <th>Owner</th>
                  <th>Engine</th>
                  <th>Size</th>
                  <th>Charset</th>
                  <th>Status</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {filteredDbs.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="rp-empty">
                        <strong>No Databases</strong>
                        <p>
                          Create a database for this account to store application
                          data.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ width: "auto", marginTop: 0 }}
                          disabled={!userId}
                          onClick={() => setShowCreate(true)}
                        >
                          + Create Database
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredDbs.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong className="mono" style={{ fontWeight: 650 }}>
                          {d.name}
                        </strong>
                      </td>
                      <td className="mono faint">
                        {d.username || selectedAccount?.username || "—"}
                      </td>
                      <td>
                        <span className="rp-status rp-status-info">
                          {d.engine}
                        </span>
                      </td>
                      <td className="mono faint">
                        {formatSize(d.sizeBytes || 0)}
                      </td>
                      <td className="faint">{d.charset}</td>
                      <td>
                        <span className="rp-status rp-status-ok">
                          <span className="rp-status-dot" />
                          Active
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            justifyContent: "flex-end"
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => void openAdminer(d.id)}
                          >
                            Manage
                          </button>
                          <div className="rp-menu-wrap">
                            <button
                              type="button"
                              className="rp-menu-btn"
                              onClick={() =>
                                setMenuId((id) =>
                                  id === d.id ? null : d.id
                                )
                              }
                            >
                              •••
                            </button>
                            {menuId === d.id ? (
                              <div className="rp-menu">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    void exportDb(d.id, d.name);
                                  }}
                                >
                                  Export SQL
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    setQueryDbId(d.id);
                                    setTab("import");
                                  }}
                                >
                                  Import SQL
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void deleteDb(d)}
                                >
                                  Delete database
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {tab === "users" ? (
        <>
          <div className="rp-toolbar">
            <input
              className="rp-toolbar-search"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              disabled={!userId}
              onClick={() => {
                setTab("wizard");
                setWizStep(2);
              }}
            >
              + Create User
            </button>
          </div>
          <div className="rp-panel">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Host</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="rp-empty">
                        <strong>No Database Users</strong>
                        <p>
                          Create a MySQL user and grant privileges on a
                          database.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ width: "auto", marginTop: 0 }}
                          onClick={() => setTab("wizard")}
                        >
                          Open Wizard
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.id}>
                      <td className="mono" style={{ fontWeight: 650 }}>
                        {u.login}
                      </td>
                      <td className="faint">{u.remoteHost}</td>
                      <td className="mono faint">{u.username || "—"}</td>
                      <td>
                        <span className="rp-status rp-status-ok">
                          <span className="rp-status-dot" />
                          Active
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => void deleteUser(u)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {tab === "wizard" ? (
        <div className="rp-panel" style={{ padding: 18 }}>
          <div className="rp-steps" style={{ marginBottom: 18 }}>
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`rp-step ${wizStep === s ? "active" : ""} ${
                  wizStep > s ? "done" : ""
                }`}
              >
                <span className="rp-step-num">
                  {wizStep > s ? "✓" : `0${s}`}
                </span>
                <span>
                  {s === 1
                    ? "Database"
                    : s === 2
                      ? "User"
                      : "Permissions"}
                </span>
              </div>
            ))}
          </div>
          <p className="faint" style={{ margin: "0 0 16px", fontSize: 13 }}>
            Create a database, create a user, and assign privileges — same flow
            as cPanel.
          </p>

          {wizStep === 1 ? (
            <form onSubmit={wizardCreateDb}>
              <div className="field" style={{ maxWidth: 360 }}>
                <label>New Database Name</label>
                <input
                  value={wizDb}
                  onChange={(e) => setWizDb(e.target.value)}
                  required
                  placeholder="wordpress"
                />
                <p className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                  Prefixed with account username automatically
                </p>
              </div>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={wizBusy || !userId}
                style={{ width: "auto", marginTop: 8 }}
              >
                {wizBusy ? "Creating…" : "Create Database"}
              </button>
            </form>
          ) : null}

          {wizStep === 2 ? (
            <form onSubmit={wizardCreateUser}>
              <div className="form-grid">
                <div className="field">
                  <label>Username</label>
                  <input
                    value={wizUser}
                    onChange={(e) => setWizUser(e.target.value)}
                    placeholder="optional (defaults to account)"
                  />
                </div>
                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={wizPass}
                    onChange={(e) => setWizPass(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={wizBusy}
                style={{ width: "auto", marginTop: 8 }}
              >
                Create User &amp; Add Privileges
              </button>
            </form>
          ) : null}

          {wizStep === 3 ? (
            <div>
              <div className="success-banner">
                Complete — database + user + ALL PRIVILEGES are ready.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ width: "auto", marginTop: 0 }}
                  onClick={() => {
                    setWizStep(1);
                    setTab("databases");
                    void openAdminer(wizDbId || undefined);
                  }}
                >
                  Open phpMyAdmin
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setWizStep(1)}
                >
                  Create another
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "import" ? (
        <div className="rp-panel" style={{ padding: 18 }}>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Target database</label>
              <select
                value={queryDbId}
                onChange={(e) => setQueryDbId(e.target.value)}
              >
                {databases.length === 0 ? (
                  <option value="">No databases</option>
                ) : null}
                {databases.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>Export</h3>
          <p className="faint" style={{ fontSize: 13, marginBottom: 10 }}>
            Download a full SQL dump of the selected database.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ width: "auto", marginTop: 0, marginBottom: 20 }}
            disabled={!queryDbId}
            onClick={() =>
              queryDbId &&
              void exportDb(
                queryDbId,
                databases.find((d) => d.id === queryDbId)?.name || "db"
              )
            }
          >
            Export Database
          </button>

          <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>Import</h3>
          <form onSubmit={importDb}>
            <div className="field">
              <label>Paste .sql contents</label>
              <textarea
                className="textarea"
                rows={10}
                value={importSql}
                onChange={(e) => setImportSql(e.target.value)}
                placeholder="-- MySQL dump..."
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              type="submit"
              disabled={!queryDbId || !importSql.trim()}
              style={{ width: "auto", marginTop: 8 }}
            >
              Import into selected database
            </button>
          </form>

          <h3 style={{ margin: "24px 0 10px", fontSize: 14 }}>SQL Console</h3>
          <form onSubmit={runQuery}>
            <div className="field">
              <label>SQL (SELECT / SHOW / DESCRIBE / EXPLAIN)</label>
              <textarea
                className="textarea"
                rows={4}
                value={sql}
                onChange={(e) => setSql(e.target.value)}
              />
            </div>
            <button
              className="btn btn-ghost btn-sm"
              type="submit"
              disabled={!queryDbId}
              style={{ width: "auto", marginTop: 8 }}
            >
              Run query
            </button>
          </form>
          {queryResult ? (
            <div className="rp-table-wrap" style={{ marginTop: 12 }}>
              <table className="rp-acct-table">
                <thead>
                  <tr>
                    {queryResult.columns.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.rows.map((r, i) => (
                    <tr key={i}>
                      {r.map((cell, j) => (
                        <td key={j} className="mono">
                          {String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "pma" ? (
        <div className="rp-panel" style={{ padding: 28, textAlign: "center" }}>
          <strong style={{ display: "block", fontSize: 16, marginBottom: 8 }}>
            phpMyAdmin
          </strong>
          <p
            className="faint"
            style={{ maxWidth: 420, margin: "0 auto 16px", fontSize: 13 }}
          >
            Launch the database administration interface for advanced SQL
            management. Secure session handoff is used where supported.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            disabled={!staff && !userId}
            onClick={() => void openAdminer()}
          >
            Open phpMyAdmin
          </button>
        </div>
      ) : null}

      {/* Create Database Drawer */}
      {showCreate ? (
        <div className="rp-drawer-bg" onClick={() => setShowCreate(false)}>
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Create Database"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>Create Database</h2>
                <p>Name, charset, optional user and privileges.</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowCreate(false)}
              >
                ✕
              </button>
            </div>
            <form className="rp-drawer-body" onSubmit={createDatabaseFlow}>
              <div className="rp-drawer-section">
                <h3>Database Information</h3>
                <div className="field">
                  <label>Database Name</label>
                  <input
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    required
                    placeholder="wordpress"
                  />
                </div>
                <div className="field">
                  <label>Charset</label>
                  <select
                    value={createCharset}
                    onChange={(e) => setCreateCharset(e.target.value)}
                  >
                    <option value="utf8mb4">utf8mb4</option>
                    <option value="utf8">utf8</option>
                    <option value="latin1">latin1</option>
                  </select>
                </div>
                <div className="field">
                  <label>Collation</label>
                  <select
                    value={createCollation}
                    onChange={(e) => setCreateCollation(e.target.value)}
                  >
                    <option value="utf8mb4_unicode_ci">
                      utf8mb4_unicode_ci
                    </option>
                    <option value="utf8mb4_general_ci">
                      utf8mb4_general_ci
                    </option>
                    <option value="utf8_general_ci">utf8_general_ci</option>
                  </select>
                </div>
              </div>
              <div className="rp-drawer-section">
                <h3>User</h3>
                <label className="rp-check">
                  <input
                    type="radio"
                    name="userMode"
                    checked={createUserMode === "new"}
                    onChange={() => setCreateUserMode("new")}
                  />
                  Create new database user
                </label>
                <label className="rp-check">
                  <input
                    type="radio"
                    name="userMode"
                    checked={createUserMode === "existing"}
                    onChange={() => setCreateUserMode("existing")}
                  />
                  Use existing user
                </label>
                <label className="rp-check">
                  <input
                    type="radio"
                    name="userMode"
                    checked={createUserMode === "none"}
                    onChange={() => setCreateUserMode("none")}
                  />
                  Database only
                </label>
                {createUserMode === "new" ? (
                  <>
                    <div className="field" style={{ marginTop: 10 }}>
                      <label>Username</label>
                      <input
                        value={createUserLogin}
                        onChange={(e) => setCreateUserLogin(e.target.value)}
                        placeholder="optional"
                      />
                    </div>
                    <div className="field">
                      <label>Password</label>
                      <input
                        type="password"
                        value={createUserPass}
                        onChange={(e) => setCreateUserPass(e.target.value)}
                        minLength={8}
                        required
                      />
                    </div>
                  </>
                ) : null}
                {createUserMode === "existing" ? (
                  <div className="field" style={{ marginTop: 10 }}>
                    <label>Existing user</label>
                    <select
                      value={createExistingUserId}
                      onChange={(e) =>
                        setCreateExistingUserId(e.target.value)
                      }
                    >
                      <option value="">Select…</option>
                      {dbUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.login}
                        </option>
                      ))}
                    </select>
                    <p className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                      Privileges can be managed after creation via phpMyAdmin.
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="rp-drawer-section">
                <h3>Permissions</h3>
                <p className="faint" style={{ fontSize: 13, margin: 0 }}>
                  New users receive <strong>Full Access</strong> on the created
                  database.
                </p>
              </div>
              <div className="rp-drawer-ft">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", marginTop: 0 }}
                  disabled={createBusy || !userId}
                >
                  {createBusy ? "Creating…" : "Create Database"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
